<script setup lang="ts">
import DocumentAccordion from "@/components/DocumentAccordion.vue"
import LoadingButton from "@/components/LoadingButton.vue"
import Pdf from "@/components/Pdf.vue"
import type { Invoice } from "@/model/order/invoice"
import { InvoiceType } from "@/model/order/invoice-type"
import type { PayOrderDataDto } from "@/model/order/pay-order-data-dto"
import type { ShopPayOrderTask } from "@/model/order/shop-pay-order-task"
import { MolliePaymentState } from "@/model/payment/mollie-payment-state"
import type { PaymentMethod } from "@/model/payment/payment-method"
import MollieRefundBox from "@/pages/Order/Payment/MollieRefundBox.vue"
import PaymentMethodSelect from "@/pages/Order/Payment/PaymentMethodSelect.vue"
import ShopOrderTable from "@/pages/Tasks/Tasks/internal/ShopOrderTable.vue"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, $date, $datetime, useApp, useHttpClient, useHttpGet, useLoadingState } from "@/vf"
import { computed, type Ref, ref, toRefs } from "vue"

const props = defineProps<TasksProps<ShopPayOrderTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const { data, refresh: refreshData } = (await useHttpGet<PayOrderDataDto>(
    `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/pay-order`,
)) as { data: Ref<PayOrderDataDto>; refresh: () => Promise<void> }

const { apiLink } = useApp()
const http = useHttpClient()

const paymentButtonsLoading = useLoadingState()
const syncOrderLoading = useLoadingState()
const openDocument = ref<string>(data.value.invoices?.length ? "" : "orderConfirmation")
const invoices = computed(() => data.value.invoices ?? [])
const allowedPaymentMethods = computed(() => data.value.allowedPaymentMethods ?? [])
const selectedPaymentMethod = ref<PaymentMethod | null>(null)

function toggleDocument(document: string) {
    openDocument.value = openDocument.value === document ? "" : document
}

function invoiceLink(invoice: Invoice) {
    return `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/invoice/${invoice.id}`
}

function invoiceTitle(invoice: Invoice) {
    const label = invoice.type === InvoiceType.CreditNote ? trans("credit_note") : trans("invoice")
    return invoice.invoiceNumber ? `${label} ${invoice.invoiceNumber}` : label
}

async function pay() {
    if (props.task.state !== "open") {
        return
    }
    paymentButtonsLoading.state = true
    const response = await http.post<{ checkoutUrl: string }>(
        `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/pay-order`,
        { paymentMethod: selectedPaymentMethod.value },
    )

    if (!response.data.checkoutUrl) {
        paymentButtonsLoading.state = false
        return
    }

    location.href = response.data.checkoutUrl

    emit("updated")
}

async function submit() {
    await http.post<{ checkoutUrl: string }>(
        `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/pay-order?submit=1`,
    )

    emit("updated")
}

async function refresh() {
    await refreshData()
    emit("updated")
}

async function syncOrder() {
    syncOrderLoading.state = true
    await http.post(`/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/sync-order`)
    await refresh()
    syncOrderLoading.state = false
}

const trans = defineTranslations({
    "pending_payments.title": {
        de: "Ausstehende Zahlungen",
        en: "Pending Payments",
    },
    "pending_payments.description": {
        de: "Hier stehen alle Zahlungen, die bereits angestoßen wurden, die aber noch nicht abgeschlossen sind.",
        en: "Here you can see all payments you have already initiated, but which are not yet completed.",
    },
    "payment_completed": {
        de: 'Vielen Dank! Die Zahlung wurde erfolgreich vollständig erfasst. Klicke auf "Weiter" um die Anmeldung abzuschließen.',
        en: 'Thank you! The payment has been successfully completed. Click "Continue" to complete the registration.',
    },
    "payment_status.paid": {
        de: "Zahlung erfolgt",
        en: "Payment completed",
    },
    "payment_status.pending": {
        de: "Zahlung wird verarbeitet",
        en: "Payment is being processed",
    },
    "payment_status.open": {
        de: "Zahlung wurde angefragt",
        en: "Payment requested",
    },
    "payment_status.expired": {
        de: "Zahlung abgelaufen",
        en: "Payment expired",
    },
    "payment_status.canceled": {
        de: "Zahlung abgebrochen",
        en: "Payment canceled",
    },
    "payment_status.prefix_date": {
        de: "Zahlung vom",
        en: "Payment from",
    },
    "action.pay_now": {
        de: "Jetzt bezahlen ({amount})",
        en: "Pay now ({amount})",
    },
    "action.complete_order": {
        de: "Bestellung abschließen",
        en: "Complete order",
    },
    "action.sync_order": {
        de: "Bestellung aktualisieren",
        en: "Update order",
    },
    "banktransfer.description": {
        de: "Bitte überweise den Betrag ausschließlich auf folgendes Konto:",
        en: "Please transfer the amount to the following account:",
    },
    "banktransfer.expiration_hint": {
        de: "Bitte beachte, dass die Zahlung bis zum {expiresAt} bei unserem Zahlungsdienstleister eingegangen sein muss und der Betrag exakt übereinstimmen muss. Ansonsten wird die Zahlung automatisch storniert und du musst hier eine neue Zahlung anstoßen.",
        en: "Please note that the payment must be received by our payment service provider by {expiresAt} and the amount must match exactly. Otherwise, the payment will be automatically canceled and you will need to initiate a new payment here.",
    },
    "banktransfer.recipient": {
        de: "Begünstigter",
        en: "Recipient",
    },
    "banktransfer.iban": {
        de: "IBAN",
        en: "IBAN",
    },
    "banktransfer.bic": {
        de: "BIC",
        en: "BIC",
    },
    "banktransfer.amount": {
        de: "Betrag",
        en: "Amount",
    },
    "banktransfer.reference": {
        de: "Verwendungszweck",
        en: "Transfer reference",
    },
    "payment_method.creditcard": {
        de: "Kreditkarte",
        en: "Credit Card",
    },
    "payment_method.paybybank": {
        de: "Sofortüberweisung",
        en: "Pay by Bank",
    },
    "payment_method.banktransfer": {
        de: "Banküberweisung",
        en: "Bank Transfer",
    },

    "order_confirmation": {
        de: "Auftragsbestätigung",
        en: "Order confirmation",
    },
    "order_confirmation_download": {
        de: "Auftragsbestätigung herunterladen",
        en: "Download order confirmation",
    },
    "invoice": {
        de: "Rechnung",
        en: "Invoice",
    },
    "invoice_download": {
        de: "Rechnung herunterladen",
        en: "Download invoice",
    },
    "credit_note": {
        de: "Gutschrift",
        en: "Credit note",
    },
    "credit_note_download": {
        de: "Gutschrift herunterladen",
        en: "Download credit note",
    },
})

function translatePaymentMethod(method: string) {
    switch (method) {
        case "creditcard":
            return trans("payment_method.creditcard")
        case "paybybank":
            return trans("payment_method.paybybank")
        case "banktransfer":
            return trans("payment_method.banktransfer")
        default:
            return method
    }
}
</script>
<template>
    <div class="p-5">
        <b>{{ $t("@tasks:tasks.shop.pay_order.description.title") }}</b>
        <div>{{ $t("@tasks:tasks.shop.pay_order.description.text") }}</div>

        <div class="row">
            <div class="col-xl-6 col-lg-6">
                <DocumentAccordion
                    :title="trans('order_confirmation')"
                    :open="openDocument === 'orderConfirmation'"
                    @toggle="toggleDocument('orderConfirmation')"
                >
                    <div class="text-center mb-3">
                        <a
                            :href="
                                apiLink(
                                    `/order/shop/${task.shopOrder?.shopConfiguration?.id}/${task.shopOrder?.id}/order-confirmation?download=1`,
                                )
                            "
                            class="btn btn-primary"
                        >
                            <i class="fa fa-fw fa-download"></i>
                            {{ trans("order_confirmation_download") }}
                        </a>
                    </div>
                    <Pdf
                        :file="`/order/shop/${task.shopOrder?.shopConfiguration?.id}/${task.shopOrder?.id}/order-confirmation`"
                    />
                </DocumentAccordion>
                <DocumentAccordion
                    v-for="invoice in invoices"
                    :key="invoice.id"
                    class="mt-3"
                    :title="invoiceTitle(invoice)"
                    :open="openDocument === invoice.id"
                    @toggle="toggleDocument(invoice.id!)"
                >
                    <div class="text-center mb-3">
                        <a :href="apiLink(invoiceLink(invoice) + '?download=1')" class="btn btn-primary">
                            <i class="fa fa-fw fa-download"></i>
                            {{
                                invoice.type === InvoiceType.CreditNote
                                    ? trans("credit_note_download")
                                    : trans("invoice_download")
                            }}
                        </a>
                    </div>
                    <Pdf :file="invoiceLink(invoice)" />
                </DocumentAccordion>
            </div>
            <div class="col-12 col-lg-6 order-first order-lg-last">
                <ShopOrderTable
                    :shop-order-id="task.shopOrder?.id!"
                    :shop-configuration-id="task.shopOrder?.shopConfiguration?.id!"
                    :assignments-editable="task.state === 'open'"
                />

                <div v-if="data?.pendingPayments?.length && data.unpaidAmount! > 0" class="mt-4">
                    <b>{{ trans("pending_payments.title") }}</b>
                    <p>{{ trans("pending_payments.description") }}</p>
                    <div class="list-group rounded">
                        <div v-for="payment in data.pendingPayments" :key="payment.id" class="list-group-item">
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="mr-3">
                                    <i
                                        class="fa fa-fw fa-check text-success"
                                        v-if="payment.state === MolliePaymentState.Paid"
                                    ></i>
                                    <i
                                        class="fa fa-fw fa-times text-danger"
                                        v-else-if="
                                            payment.state === MolliePaymentState.Canceled ||
                                            payment.state === MolliePaymentState.Expired
                                        "
                                    ></i>
                                    <i class="far fa-fw fa-clock" v-else></i>
                                </div>
                                <div class="flex-grow-1">
                                    <div class="text-success" v-if="payment.state === MolliePaymentState.Paid">
                                        {{ trans("payment_status.paid") }}
                                    </div>
                                    <div class="" v-else-if="payment.state === MolliePaymentState.Pending">
                                        {{ trans("payment_status.pending") }}
                                    </div>
                                    <div class="text-success" v-else-if="payment.state === MolliePaymentState.Open">
                                        {{ trans("payment_status.open") }}
                                    </div>
                                    <div class="text-danger" v-else-if="payment.state === MolliePaymentState.Expired">
                                        {{ trans("payment_status.expired") }}
                                    </div>
                                    <div class="text-danger" v-else-if="payment.state === MolliePaymentState.Canceled">
                                        {{ trans("payment_status.canceled") }}
                                    </div>
                                    <div class="text-muted small">
                                        <template v-if="payment.paymentMethod">
                                            {{ translatePaymentMethod(payment.paymentMethod) }}
                                            &nbsp;·&nbsp;
                                        </template>
                                        {{ trans("payment_status.prefix_date") }} {{ $datetime(payment.createdAt) }}
                                    </div>
                                </div>

                                <div class="badge badge-primary badge-pill text-white">
                                    {{ $currency(payment.amount, 100, { currencyDisplay: "name" }) }}
                                </div>
                            </div>
                            <div
                                v-if="
                                    payment.paymentMethod === 'banktransfer' &&
                                    payment.paymentDetails &&
                                    payment.state === MolliePaymentState.Open
                                "
                                class="mt-4"
                            >
                                <hr />
                                {{ trans("banktransfer.description") }}

                                <table class="table table-borderless">
                                    <tbody>
                                        <tr>
                                            <th>{{ trans("banktransfer.recipient") }}</th>
                                            <td>{{ payment.paymentDetails.bankName }}</td>
                                        </tr>
                                        <tr>
                                            <th>{{ trans("banktransfer.iban") }}</th>
                                            <td>{{ payment.paymentDetails.bankAccount }}</td>
                                        </tr>
                                        <tr>
                                            <th>{{ trans("banktransfer.bic") }}</th>
                                            <td>{{ payment.paymentDetails.bankBic }}</td>
                                        </tr>
                                        <tr>
                                            <th>{{ trans("banktransfer.reference") }}</th>
                                            <td>{{ payment.paymentDetails.transferReference }}</td>
                                        </tr>
                                        <tr>
                                            <th>{{ trans("banktransfer.amount") }}</th>
                                            <td>{{ $currency(payment.amount!) }}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <p class="mt-4" v-if="payment.expiresAt">
                                    {{
                                        trans("banktransfer.expiration_hint", {
                                            expiresAt: $date(payment.expiresAt),
                                        })
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <PaymentMethodSelect
                    v-if="allowedPaymentMethods.length && (data?.unpaidAmount ?? 0) > 0"
                    v-model="selectedPaymentMethod"
                    :methods="allowedPaymentMethods"
                    class="mt-4"
                />

                <div class="text-right mt-5">
                    <LoadingButton
                        :loading="paymentButtonsLoading"
                        class="btn btn-primary mt-3 ml-auto"
                        @click="pay"
                        v-if="(data?.unpaidAmount ?? 0) > 0"
                        :disabled="
                            task.state !== 'open' || (allowedPaymentMethods.length > 0 && !selectedPaymentMethod)
                        "
                    >
                        {{ trans("action.pay_now", { amount: $currency(data?.unpaidAmount) }) }}
                    </LoadingButton>
                    <div v-if="(data?.unpaidAmount ?? 0) <= 0">
                        <div class="alert alert-succcess">
                            {{ trans("payment_completed") }}
                        </div>
                        <button class="btn btn-primary" @click="submit()" v-if="task.state === 'open'">
                            {{ trans("action.complete_order") }}
                        </button>
                    </div>
                </div>

                <div v-if="props.viewMode === 'admin'" class="text-right mt-4">
                    <LoadingButton :loading="syncOrderLoading" class="btn btn-secondary" @click="syncOrder">
                        <i class="fa fa-fw fa-sync"></i>
                        {{ trans("action.sync_order") }}
                    </LoadingButton>
                </div>

                <MollieRefundBox
                    class="my-5 bg-white"
                    v-if="props.viewMode === 'admin' && task.shopOrder?.shopConfiguration?.id && task.shopOrder?.id"
                    :data-endpoint="`/order/shop/${task.shopOrder.shopConfiguration.id}/${task.shopOrder.id}/refund-data`"
                    :refund-endpoint="`/order/shop/${task.shopOrder.shopConfiguration.id}/${task.shopOrder.id}/refund`"
                    @refunded="refresh"
                />
            </div>
        </div>
    </div>
</template>
